import { Redirect, Router, WindowLocation } from '@reach/router';
import React, { useState } from 'react';

import permissions from 'common/permissions';
import Dashboard from 'components/pages/Dashboard';
import Invoicing from 'components/pages/Invoicing';
import Reports from 'components/pages/Reports';
import VehicleCollectionBrowser from 'components/pages/VehicleCollectionBrowser';
import HamburgerMenu from 'components/shared/HamburgerMenu';
import Header from 'components/shared/Header';
import NotificationCenter from 'components/shared/NotificationCenter';
import { usePermissions, useWindowSize } from 'hooks';

import {
  DASHBOARD_RELATIVE,
  INVENTORY_RELATIVE,
  INVOICING_RELATIVE,
  REPORTS_RELATIVE,
} from './routes';
import VehicleDetailsRouter from './VehicleDetailsRouter';

interface ReachComponentProps {
  default?: boolean;
  location?: WindowLocation;
  path: string;
  rooftopId?: string;
}

const VehicleCollectionView: React.FC<ReachComponentProps> = () => (
  <Router className="full-height">
    <VehicleCollectionBrowser default />
    <VehicleDetailsRouter path=":vehicleId/*" />
  </Router>
);

const RooftopPage: React.FC<ReachComponentProps> = (props) => {
  const { hasPermission } = usePermissions();

  const [isNotificationCenterOpen, setIsNotificationCenterOpen] =
    useState(false);

  const [isOpenHamburgerMenu, setIsOpenHamburgerMenu] = useState(false);

  const windowSize = useWindowSize();

  const showDashboard = React.useMemo(
    () =>
      [
        permissions.PLUGIN_VELOCITYENGAGE_VIEW_DASHBOARD,
        permissions.DASHBOARD_INVENTORY_NEEDING_ATTENTION_VIEW,
        permissions.DASHBOARD_SUMMARY_VIEW,
      ].filter((perm) => hasPermission(perm)).length > 0,
    [hasPermission]
  );

  const viewportClassName = windowSize.isMobileViewport()
    ? 'mobile-viewport overflow-hidden'
    : 'desktop-viewport';

  return (
    <div className={`page flex-rows ${viewportClassName}`}>
      <Header
        isOpenHamburgerMenu={isOpenHamburgerMenu}
        setIsOpenHamburgerMenu={setIsOpenHamburgerMenu}
        isNotificationCenterOpen={isNotificationCenterOpen}
        onClickNotificationBell={() => setIsNotificationCenterOpen(true)}
      />
      <div className="flex-grow relative header-margin">
        {hasPermission(permissions.NOTIFICATIONS_VIEW) && (
          <NotificationCenter
            isOpen={isNotificationCenterOpen}
            onClose={() => setIsNotificationCenterOpen(false)}
          />
        )}
        <HamburgerMenu
          isOpen={isOpenHamburgerMenu}
          setIsOpen={setIsOpenHamburgerMenu}
        />
        <Router className="full-height full-width vertical-scroll">
          {/* we need a DASHBOARD_VIEW permission */}
          {showDashboard && <Dashboard path={`${DASHBOARD_RELATIVE}/*`} />}
          {(hasPermission(permissions.RECON_VIEW) ||
            hasPermission(permissions.INVENTORY_VIEW)) && (
            <VehicleCollectionView path={`${INVENTORY_RELATIVE}/*`} />
          )}
          {(hasPermission(permissions.REPORTS_STEP_REPORT_VIEW) ||
            hasPermission(
              permissions.PLUGIN_VELOCITYENGAGE_CORPORATE_REPORT_VIEW
            ) ||
            hasPermission(permissions.PLUGIN_VELOCITYENGAGE_VIEW)) && (
            <Reports path={`${REPORTS_RELATIVE}/*`} />
          )}
          {hasPermission(permissions.INVOICING_VIEW) && (
            <Invoicing path={`${INVOICING_RELATIVE}/*`} />
          )}
          <Redirect
            from="*"
            to={`/${props.rooftopId}/${INVENTORY_RELATIVE}`}
            default
            noThrow
          />
        </Router>
      </div>
    </div>
  );
};

export default RooftopPage;
